import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { firstValueFrom, Subscription } from 'rxjs';
import { AdminClassQuizView, ApiService, QuizQuestionUpdate } from '../../../../core/services/api/api.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';
import { EnfafCheckboxComponent } from '../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafInputComponent } from '../../../../components/enfaf-input/enfaf-input.component';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';

const POLLING_INTERVAL_MS = 3000;
const POLLING_MAX_MS = 120000;

interface QuestionFormGroup {
  quizQuestionId: FormControl<string>;
  statement: FormControl<string>;
  optionsText: FormControl<string>; // comma-separated options
  correctOptionIndex: FormControl<number>;
  isRequired: FormControl<boolean>;
}

@Component({
  selector: 'app-admin-quiz-questions',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    EnfafIconComponent,
    EnfafButtonComponent,
    EnfafCheckboxComponent,
    EnfafInputComponent,
    EnfafSwitchDarkComponent
  ],
  templateUrl: './admin-quiz-questions.component.html',
  styleUrl: './admin-quiz-questions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminQuizQuestionsComponent implements OnDestroy {
  courseClassId = input.required<string>();
  isBulkGenerating = input(false);
  hasVideo = input(false);

  quizHasChanges = output<boolean>();
  quizReviewStateChanged = output<boolean>();

  quizStatus = signal<string>('pending');
  quizExcluded = signal(false);
  quizReviewedAt = signal<string | null>(null);
  quizError = signal<string | null>(null);
  quizErrorFriendly = signal<string | null>(null);
  questions = signal<AdminClassQuizView['questions']>([]);
  isLoading = signal(true);
  isGenerating = signal(false);
  isEditing = signal(false);
  isSaving = signal(false);
  isDeleting = signal(false);
  isMarkingReviewed = signal(false);
  isDeletingQuestion = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  isCardExpanded = signal(true);

  visibleControl = new FormControl<boolean>(true, { nonNullable: true });
  private originalVisible = signal(true);
  private pendingQuestionsUpdate = signal<QuizQuestionUpdate[] | null>(null);

  get isDirty(): boolean {
    return this.pendingQuestionsUpdate() !== null || this.visibleControl.value !== this.originalVisible();
  }

  private emitDirty(dirty: boolean): void {
    this.quizHasChanges.emit(dirty);
  }

  questionsForm = new FormArray<FormGroup<QuestionFormGroup>>([]);

  private api = inject(ApiService);
  private alert = inject(AlertService);
  private toast = inject(ToastService);
  private el = inject(ElementRef);

  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private pollingStart = 0;
  private optionsSubs: Subscription[] = [];

  private static readonly MAX_OPTIONS = 2;

  private static readonly minOptionsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '');
    const optionsCount = raw
      .split(/;|\n/)
      .map((o) => o.trim())
      .filter((o) => o.length > 0).length;
    return optionsCount >= AdminQuizQuestionsComponent.MAX_OPTIONS ? null : { minOptions: true };
  };

  constructor() {
    effect(() => {
      const id = this.courseClassId();
      if (id) {
        this.loadQuizData(id);
      }
    });

    effect(() => {
      if (this.isBulkGenerating()) {
        this.visibleControl.disable();
      } else {
        this.visibleControl.enable();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.clearOptionsSubs();
  }

  private clearOptionsSubs(): void {
    this.optionsSubs.forEach((s) => s.unsubscribe());
    this.optionsSubs = [];
  }

  toggleCard(): void {
    if (this.isNonExpandableState()) return;
    this.isCardExpanded.set(!this.isCardExpanded());
  }

  isNonExpandableState(): boolean {
    return this.quizStatus() === 'pending' && this.questions().length === 0 && !this.quizError();
  }

  isPendingReview(): boolean {
    return this.quizStatus() === 'ready' && !this.quizReviewedAt();
  }

  async loadQuizData(courseClassId?: string): Promise<void> {
    const classId = courseClassId ?? this.courseClassId();
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const view = await firstValueFrom(this.api.getAdminClassQuizView(classId));
      this.quizStatus.set(view.quizStatus);
      this.quizExcluded.set(view.quizExcluded);
      this.quizReviewedAt.set(view.quizReviewedAt);
      this.visibleControl.setValue(!view.quizExcluded);
      this.originalVisible.set(!view.quizExcluded);
      this.pendingQuestionsUpdate.set(null);
      this.emitDirty(false);
      this.quizError.set(view.quizError);
      this.quizErrorFriendly.set(this.toFriendlyQuizError(view.quizError));
      this.questions.set(view.questions);
      this.isCardExpanded.set(!this.isNonExpandableState());
      this.emitReviewState();

      if (view.quizStatus === 'processing') {
        this.isGenerating.set(true);
        this.startPolling();
      }
    } catch (err: any) {
      if (err.status === 403) {
        // Quiz feature disabled — hide silently
        this.quizStatus.set('disabled');
        this.quizReviewedAt.set(null);
        this.emitReviewState();
      } else {
        this.errorMessage.set('Error cargando datos de autoevaluación');
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  private emitReviewState(): void {
    this.quizReviewStateChanged.emit(this.isPendingReview());
  }

  async onGenerate(): Promise<void> {
    const hasExisting = this.questions().length > 0;

    if (hasExisting) {
      const confirmed = await this.alert.presentConfirmActionAlert(
        'Regenerar autoevaluación',
        'Se reemplazarán todas las preguntas actuales con nuevas preguntas generadas por IA. ¿Continuar?',
        'Cancelar',
        'Regenerar'
      );
      if (!confirmed) return;
    }

    if (this.isEditing()) {
      this.onCancelEdit();
    }

    this.isCardExpanded.set(true);
    this.isGenerating.set(true);
    this.errorMessage.set(null);
    this.quizError.set(null);
    this.quizErrorFriendly.set(null);

    try {
      await firstValueFrom(this.api.generateClassQuizAdmin(this.courseClassId()));
      this.quizStatus.set('processing');
      this.isCardExpanded.set(true);
      this.startPolling();
    } catch (err: any) {
      this.isGenerating.set(false);
      if (err.status === 400) {
        this.questions.set([]);
        this.quizStatus.set('error');
        this.quizError.set(err?.error?.message ?? 'Transcript not available for this class');
        this.quizErrorFriendly.set('No hay transcripción disponible para esta clase');
        this.errorMessage.set(null);
      } else {
        this.errorMessage.set('Error al generar la autoevaluación');
      }
    }
  }

  private scrollToActionArea(): void {
    setTimeout(() => {
      const body = this.el.nativeElement.querySelector('.quiz-card') as HTMLDivElement | null;

      if (!body) return;

      body.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }, 50);
  }

  onStartEdit(): void {
    this.isEditing.set(true);
    this.questionsForm.clear();
    this.clearOptionsSubs();
    this.scrollToActionArea();

    for (const q of this.questions()) {
      const group = new FormGroup<QuestionFormGroup>({
        quizQuestionId: new FormControl(q.quizQuestionId, { nonNullable: true }),
        statement: new FormControl(q.statement, { nonNullable: true, validators: [Validators.required] }),
        optionsText: new FormControl(q.options.join('; '), {
          nonNullable: true,
          validators: [Validators.required, AdminQuizQuestionsComponent.minOptionsValidator]
        }),
        correctOptionIndex: new FormControl(q.correctOptionIndex, { nonNullable: true }),
        isRequired: new FormControl(q.isRequired, { nonNullable: true })
      });

      // Clamp optionsText to at most 2 separator-delimited options while the user types
      const sub = group.controls.optionsText.valueChanges.subscribe((val) => {
        // Normalize Enter into "; " so typing first option + Enter continues inline.
        const normalized = val.replace(/\r?\n+/g, '; ');

        let separatorCount = 0;
        let cutPos = -1;
        for (let i = 0; i < normalized.length; i++) {
          if (normalized[i] === ';') {
            separatorCount++;
            if (separatorCount === 2) {
              cutPos = i;
              break;
            }
          }
        }

        const nextValue = cutPos !== -1 ? normalized.substring(0, cutPos) : normalized;
        if (nextValue !== val) {
          group.controls.optionsText.setValue(nextValue, { emitEvent: false });
        }
      });
      this.optionsSubs.push(sub);

      this.questionsForm.push(group);
    }
  }

  onCancelEdit(): void {
    this.questionsForm.clear();
    this.clearOptionsSubs();
    this.isEditing.set(false);
    this.scrollToActionArea();
  }

  /** Parse the semicolon/Enter-separated options text into a trimmed string array (max 2). */
  parsedOptions(group: FormGroup<QuestionFormGroup>): string[] {
    return group.controls.optionsText.value
      .split(/;|\n/)
      .map((o) => o.trim())
      .filter((o) => o.length > 0)
      .slice(0, AdminQuizQuestionsComponent.MAX_OPTIONS);
  }

  /** Letter label (A, B, C, ...) for a 0-based option index. */
  optionLabel(index: number): string {
    return String.fromCharCode(65 + index);
  }

  onDeleteOption(group: FormGroup<QuestionFormGroup>, index: number): void {
    const options = this.parsedOptions(group);
    options.splice(index, 1);
    group.controls.optionsText.setValue(options.join('; '));
    // If the correct option was deleted or is now out of range, reset to first
    if (group.controls.correctOptionIndex.value >= options.length) {
      group.controls.correctOptionIndex.setValue(0);
    }
  }

  onOptionsEnter(event: Event, group: FormGroup<QuestionFormGroup>): void {
    event.preventDefault();

    const raw = group.controls.optionsText.value ?? '';
    const trimmed = raw.trim();
    if (!trimmed) return;

    const options = this.parsedOptions(group);
    if (options.length >= AdminQuizQuestionsComponent.MAX_OPTIONS) return;

    if (/[;\s]$/.test(raw)) {
      // Keep a single trailing space after semicolon if user already typed separator.
      group.controls.optionsText.setValue(raw.replace(/;\s*$/, '; '));
      return;
    }

    group.controls.optionsText.setValue(`${raw}; `);
  }

  hasFormValidationErrors(): boolean {
    return this.questionsForm.controls.some((group) => {
      const statementInvalid = group.controls.statement.invalid;
      const optionsInvalid = group.controls.optionsText.invalid;
      const options = this.parsedOptions(group);
      const selectedCorrect = group.controls.correctOptionIndex.value;
      const correctOptionInvalid = selectedCorrect < 0 || selectedCorrect >= options.length;
      return statementInvalid || optionsInvalid || correctOptionInvalid;
    });
  }

  async onSave(): Promise<void> {
    if (this.hasFormValidationErrors()) {
      this.questionsForm.markAllAsTouched();
      this.toast.presentErrorToast('Revisa las preguntas: no puede haber enunciados ni respuestas vacías');
      return;
    }

    const updates: QuizQuestionUpdate[] = [];
    for (const group of this.questionsForm.controls) {
      const v = group.getRawValue();
      const options = this.parsedOptions(group);
      if (v.correctOptionIndex < 0 || v.correctOptionIndex >= options.length) {
        this.toast.presentErrorToast('Selecciona la respuesta correcta para cada pregunta');
        return;
      }
      updates.push({
        quizQuestionId: v.quizQuestionId,
        statement: v.statement.trim(),
        options,
        correctOptionIndex: v.correctOptionIndex,
        isRequired: v.isRequired
      });
    }

    try {
      this.isSaving.set(true);
      await firstValueFrom(this.api.updateClassQuizQuestions(this.courseClassId(), updates));
      this.questions.set(updates.map((u, i) => ({ ...u, position: i })));
      this.pendingQuestionsUpdate.set(null);
      this.isEditing.set(false);
      this.scrollToActionArea();
      this.emitDirty(this.isDirty);
      this.toast.presentSuccessToast('Preguntas guardadas correctamente');
    } catch {
      this.toast.presentErrorToast('Error al guardar las preguntas. Inténtalo de nuevo.');
    } finally {
      this.isSaving.set(false);
    }
  }

  onToggleVisible(visible: boolean): void {
    this.quizExcluded.set(!visible);
    this.emitDirty(this.isDirty);
  }

  async confirmDeleteQuiz(): Promise<void> {
    const confirmed = await this.alert.presentConfirmRemoveActionAlert({
      title: 'Eliminar autoevaluación',
      message: '¿Eliminar la autoevaluación generada por IA?',
      message2: 'Esta acción no se puede deshacer.',
      cancelText: 'Cancelar',
      confirmText: 'Eliminar'
    });

    if (confirmed) {
      await this.deleteQuiz();
    }
  }

  async deleteQuiz(): Promise<void> {
    if (this.isDeleting()) return;
    this.isDeleting.set(true);

    try {
      await firstValueFrom(this.api.deleteAdminClassQuiz(this.courseClassId()));
      this.questions.set([]);
      this.quizStatus.set('pending');
      this.quizReviewedAt.set(null);
      this.quizError.set(null);
      this.quizErrorFriendly.set(null);
      this.errorMessage.set(null);
      this.pendingQuestionsUpdate.set(null);
      this.isEditing.set(false);
      this.emitDirty(this.isDirty);
      this.emitReviewState();
      this.toast.presentSuccessToast('Autoevaluación eliminada correctamente');
    } catch {
      this.toast.presentErrorToast('Error al eliminar la autoevaluación');
    } finally {
      this.isDeleting.set(false);
    }
  }

  /** Delete a single question in read mode — calls the API and removes it locally. */
  async deleteReadQuestion(question: AdminClassQuizView['questions'][number]): Promise<void> {
    const confirmed = await this.alert.presentConfirmRemoveActionAlert({
      title: 'Eliminar pregunta',
      message: '¿Eliminar esta pregunta?',
      message2: 'Esta acción no se puede deshacer.',
      cancelText: 'Cancelar',
      confirmText: 'Eliminar'
    });
    if (!confirmed) return;

    this.isDeletingQuestion.set(question.quizQuestionId);
    try {
      await firstValueFrom(this.api.deleteAdminClassQuizQuestion(question.quizQuestionId));
      const remaining = this.questions().filter((q) => q.quizQuestionId !== question.quizQuestionId);
      this.questions.set(remaining);
      if (remaining.length === 0) {
        this.quizStatus.set('pending');
        this.quizReviewedAt.set(null);
        this.emitReviewState();
      }
      this.toast.presentSuccessToast('Pregunta eliminada correctamente');
    } catch {
      this.toast.presentErrorToast('Error al eliminar la pregunta');
    } finally {
      this.isDeletingQuestion.set(null);
    }
  }

  /** Remove a question from the edit-mode FormArray (no API call — save persists the changes). */
  deleteEditQuestion(index: number): void {
    this.questionsForm.removeAt(index);
  }

  async markAsReviewed(): Promise<void> {
    if (this.isMarkingReviewed()) return;
    this.isMarkingReviewed.set(true);
    try {
      const result = await firstValueFrom(this.api.markClassQuizAsReviewed(this.courseClassId()));
      this.quizReviewedAt.set(result.quizReviewedAt);
      this.emitReviewState();
      this.toast.presentSuccessToast('Autoevaluación marcada como revisada');
    } catch {
      this.toast.presentErrorToast('Error al marcar la autoevaluación como revisada');
    } finally {
      this.isMarkingReviewed.set(false);
    }
  }

  async saveChanges(): Promise<void> {
    const saveOps: Promise<unknown>[] = [];

    const pending = this.pendingQuestionsUpdate();
    if (pending !== null) {
      saveOps.push(
        firstValueFrom(this.api.updateClassQuizQuestions(this.courseClassId(), pending)).then(() => {
          this.pendingQuestionsUpdate.set(null);
        })
      );
    }

    if (this.visibleControl.value !== this.originalVisible()) {
      const excluded = !this.visibleControl.value;
      saveOps.push(
        firstValueFrom(this.api.setClassQuizExcluded(this.courseClassId(), excluded)).then(() => {
          this.originalVisible.set(!excluded);
        })
      );
    }

    await Promise.all(saveOps);
    this.emitDirty(false);
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingStart = Date.now();

    this.pollingInterval = setInterval(async () => {
      if (Date.now() - this.pollingStart > POLLING_MAX_MS) {
        this.stopPolling();
        this.isGenerating.set(false);
        this.errorMessage.set('La generación está tardando demasiado. Recarga la página.');
        return;
      }

      try {
        const view = await firstValueFrom(this.api.getAdminClassQuizView(this.courseClassId()));
        this.quizStatus.set(view.quizStatus);
        this.quizError.set(view.quizError);
        this.quizErrorFriendly.set(this.toFriendlyQuizError(view.quizError));
        this.questions.set(view.questions);
        this.quizReviewedAt.set(view.quizReviewedAt);

        if (view.quizStatus !== 'processing') {
          this.stopPolling();
          this.isGenerating.set(false);

          if (view.quizStatus === 'ready') {
            this.toast.presentSuccessToast('Autoevaluación generada correctamente');
          } else if (view.quizStatus === 'error') {
            this.errorMessage.set(view.quizError ?? 'Error en la generación');
          }

          this.emitReviewState();
        }
      } catch {
        // Ignore polling errors
      }
    }, POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private toFriendlyQuizError(quizError: string | null): string | null {
    if (!quizError) return null;

    const normalized = quizError.toLowerCase();
    if (normalized.includes('transcript not available')) {
      return 'No hay transcripción disponible para esta clase';
    }

    if (normalized.includes('minimum 1 required') || normalized.includes('only 0 questions generated')) {
      return 'Contenido insuficiente: no se ha podido generar al menos una pregunta con la transcripción actual.';
    }

    if (normalized.includes('max questions is set to 0')) {
      return 'La generación de quiz está desactivada: el máximo de preguntas está configurado a 0 en los ajustes.';
    }

    return null;
  }
}
